import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MotionType } from "./motionType";
class RigidBodyConstructionInfoInner {
    _wasmInstance;
    _ptr;
    _shapeReference;
    constructor(wasmInstance, ptr) {
        this._wasmInstance = wasmInstance;
        this._ptr = ptr;
        this._shapeReference = null;
    }
    dispose() {
        if (this._ptr === 0) {
            return;
        }
        this._wasmInstance.deref()?.deallocateBuffer(this._ptr, 144 /* Constants.RigidBodyConstructionInfoSize */);
        this._ptr = 0;
        if (this._shapeReference) {
            this._shapeReference.removeReference();
        }
        this._shapeReference = null;
    }
    get ptr() {
        return this._ptr;
    }
    get shape() {
        return this._shapeReference;
    }
    set shape(value) {
        if (this._shapeReference) {
            this._shapeReference.removeReference();
        }
        this._shapeReference = value;
        if (this._shapeReference) {
            this._shapeReference.addReference();
        }
    }
}
function RigidBodyConstructionInfoFinalizer(inner) {
    inner.dispose();
}
const RigidBodyConstructionInfoRegistryMap = new WeakMap();
/**
 * RigidBodyConstructionInfo is used to create a rigid body in the physics engine
 *
 * it contains all the information needed to create a rigid body, such as the shape, mass, inertia, damping, etc.
 */
export class RigidBodyConstructionInfo {
    _wasmInstance;
    _uint32Ptr;
    _float32Ptr;
    _uint8Ptr;
    _uint16Ptr;
    _inner;
    /**
     * Creates a new RigidBodyConstructionInfo
     * @param wasmInstance The BulletWasmInstance to use
     */
    constructor(wasmInstance) {
        this._wasmInstance = wasmInstance;
        // Allocate buffer
        const ptr = wasmInstance.allocateBuffer(144 /* Constants.RigidBodyConstructionInfoSize */);
        this._uint32Ptr = wasmInstance.createTypedArray(Uint32Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 4 /* Constants.A32BytesPerElement */);
        this._float32Ptr = wasmInstance.createTypedArray(Float32Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 4 /* Constants.A32BytesPerElement */);
        this._uint8Ptr = wasmInstance.createTypedArray(Uint8Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 1 /* Constants.A8BytesPerElement */);
        this._uint16Ptr = wasmInstance.createTypedArray(Uint16Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 2 /* Constants.A16BytesPerElement */);
        this._inner = new RigidBodyConstructionInfoInner(new WeakRef(wasmInstance), ptr);
        // Initialize to default values
        const uint32Ptr = this._uint32Ptr.array;
        const float32Ptr = this._float32Ptr.array;
        const uint8Ptr = this._uint8Ptr.array;
        const uint16Ptr = this._uint16Ptr.array;
        // shape
        uint32Ptr[0 /* RigidBodyConstructionInfoOffsets.Shape */ / 4 /* Constants.A32BytesPerElement */] = 0;
        // initial_transform
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 0] = 1;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 1] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 2] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 3] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 4] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 5] = 1;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 6] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 7] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 8] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 9] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 10] = 1;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 11] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 12] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 13] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 14] = 0;
        float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 15] = 1;
        // dataMask
        uint16Ptr[80 /* RigidBodyConstructionInfoOffsets.DataMask */ / 2 /* Constants.A16BytesPerElement */] = 0x0000;
        // motionType
        uint8Ptr[82 /* RigidBodyConstructionInfoOffsets.MotionType */ / 1 /* Constants.A8BytesPerElement */] = MotionType.Dynamic;
        // mass
        float32Ptr[84 /* RigidBodyConstructionInfoOffsets.Mass */ / 4 /* Constants.A32BytesPerElement */] = 1.0;
        // localInertia
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 0] = 0;
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 1] = 0;
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 2] = 0;
        // linearDamping
        float32Ptr[100 /* RigidBodyConstructionInfoOffsets.LinearDamping */ / 4 /* Constants.A32BytesPerElement */] = 0;
        // angularDamping
        float32Ptr[104 /* RigidBodyConstructionInfoOffsets.AngularDamping */ / 4 /* Constants.A32BytesPerElement */] = 0;
        // friction
        float32Ptr[108 /* RigidBodyConstructionInfoOffsets.Friction */ / 4 /* Constants.A32BytesPerElement */] = 0.5;
        // restitution
        float32Ptr[112 /* RigidBodyConstructionInfoOffsets.Restitution */ / 4 /* Constants.A32BytesPerElement */] = 0.0;
        // linearSleepingThreshold
        float32Ptr[116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */] = 0.0;
        // angularSleepingThreshold
        float32Ptr[120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */] = 1.0;
        // collisionGroup
        uint16Ptr[124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */ / 2 /* Constants.A16BytesPerElement */] = 1 << 0;
        // collisionMask
        uint16Ptr[126 /* RigidBodyConstructionInfoOffsets.CollisionMask */ / 2 /* Constants.A16BytesPerElement */] = 0xFFFF;
        // additionalDamping
        uint8Ptr[128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */ / 1 /* Constants.A8BytesPerElement */] = +false;
        // noContactResponse
        uint8Ptr[129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */ / 1 /* Constants.A8BytesPerElement */] = +false;
        // disableDeactivation
        uint8Ptr[130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */ / 1 /* Constants.A8BytesPerElement */] = +false;
        // finalization registry
        let registry = RigidBodyConstructionInfoRegistryMap.get(wasmInstance);
        if (registry === undefined) {
            registry = new FinalizationRegistry(RigidBodyConstructionInfoFinalizer);
            RigidBodyConstructionInfoRegistryMap.set(wasmInstance, registry);
        }
        registry.register(this, this._inner, this);
    }
    /**
     * Disposes the RigidBodyConstructionInfo
     */
    dispose() {
        if (this._inner.ptr === 0) {
            return;
        }
        this._inner.dispose();
        const registry = RigidBodyConstructionInfoRegistryMap.get(this._wasmInstance);
        registry?.unregister(this);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._inner.ptr;
    }
    _nullCheck() {
        if (this._inner.ptr === 0) {
            throw new Error("Cannot access disposed RigidBodyConstructionInfo");
        }
    }
    /**
     * The shape of the rigid body
     */
    get shape() {
        this._nullCheck();
        return this._inner.shape;
    }
    set shape(value) {
        this._nullCheck();
        this._inner.shape = value;
        this._uint32Ptr.array[0 /* RigidBodyConstructionInfoOffsets.Shape */ / 4 /* Constants.A32BytesPerElement */] = value ? value.ptr : 0;
    }
    /**
     * Gets the initial transform of the rigid body
     * @param result The matrix to store the result
     * @returns The initial transform of the rigid body
     */
    getInitialTransformToRef(result) {
        this._nullCheck();
        const float32Ptr = this._float32Ptr.array;
        result.set(float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 0], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 1], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 2], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 3], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 4], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 5], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 6], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 7], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 8], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 9], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 10], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 11], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 12], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 13], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 14], float32Ptr[16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */ + 15]);
        return result;
    }
    /**
     * Sets the initial transform of the rigid body
     * @param value The initial transform of the rigid body
     */
    setInitialTransform(value) {
        this._nullCheck();
        const float32Ptr = this._float32Ptr.array;
        value.copyToArray(float32Ptr, 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */ / 4 /* Constants.A32BytesPerElement */);
    }
    /**
     * The motion type of the rigid body
     */
    get motionType() {
        this._nullCheck();
        return this._uint8Ptr.array[82 /* RigidBodyConstructionInfoOffsets.MotionType */ / 1 /* Constants.A8BytesPerElement */];
    }
    set motionType(value) {
        this._nullCheck();
        this._uint8Ptr.array[82 /* RigidBodyConstructionInfoOffsets.MotionType */ / 1 /* Constants.A8BytesPerElement */] = value;
    }
    /**
     * The mass of the rigid body
     */
    get mass() {
        this._nullCheck();
        return this._float32Ptr.array[84 /* RigidBodyConstructionInfoOffsets.Mass */ / 4 /* Constants.A32BytesPerElement */];
    }
    set mass(value) {
        this._nullCheck();
        this._float32Ptr.array[84 /* RigidBodyConstructionInfoOffsets.Mass */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The local inertia of the rigid body
     *
     * If the local inertia is not set, it will be calculated from the shape
     */
    get localInertia() {
        this._nullCheck();
        const maskValue = this._uint16Ptr.array[80 /* RigidBodyConstructionInfoOffsets.DataMask */ / 2 /* Constants.A16BytesPerElement */];
        if ((maskValue & 1 /* ConstructionInfoDataMask.LocalInertia */) === 0) {
            return null;
        }
        const float32Ptr = this._float32Ptr.array;
        const x = float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 0];
        const y = float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 1];
        const z = float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 2];
        return new Vector3(x, y, z);
    }
    set localInertia(value) {
        this._nullCheck();
        if (value === null) {
            this._uint16Ptr.array[80 /* RigidBodyConstructionInfoOffsets.DataMask */ / 2 /* Constants.A16BytesPerElement */] &= ~1 /* ConstructionInfoDataMask.LocalInertia */;
            return;
        }
        this._uint16Ptr.array[80 /* RigidBodyConstructionInfoOffsets.DataMask */ / 2 /* Constants.A16BytesPerElement */] |= 1 /* ConstructionInfoDataMask.LocalInertia */;
        const float32Ptr = this._float32Ptr.array;
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 0] = value.x;
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 1] = value.y;
        float32Ptr[88 /* RigidBodyConstructionInfoOffsets.LocalInertia */ / 4 /* Constants.A32BytesPerElement */ + 2] = value.z;
    }
    /**
     * The linear damping of the rigid body
     */
    get linearDamping() {
        this._nullCheck();
        return this._float32Ptr.array[100 /* RigidBodyConstructionInfoOffsets.LinearDamping */ / 4 /* Constants.A32BytesPerElement */];
    }
    set linearDamping(value) {
        this._nullCheck();
        this._float32Ptr.array[100 /* RigidBodyConstructionInfoOffsets.LinearDamping */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The angular damping of the rigid body
     */
    get angularDamping() {
        this._nullCheck();
        return this._float32Ptr.array[104 /* RigidBodyConstructionInfoOffsets.AngularDamping */ / 4 /* Constants.A32BytesPerElement */];
    }
    set angularDamping(value) {
        this._nullCheck();
        this._float32Ptr.array[104 /* RigidBodyConstructionInfoOffsets.AngularDamping */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The friction of the rigid body
     */
    get friction() {
        this._nullCheck();
        return this._float32Ptr.array[108 /* RigidBodyConstructionInfoOffsets.Friction */ / 4 /* Constants.A32BytesPerElement */];
    }
    set friction(value) {
        this._nullCheck();
        this._float32Ptr.array[108 /* RigidBodyConstructionInfoOffsets.Friction */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The restitution of the rigid body
     */
    get restitution() {
        this._nullCheck();
        return this._float32Ptr.array[112 /* RigidBodyConstructionInfoOffsets.Restitution */ / 4 /* Constants.A32BytesPerElement */];
    }
    set restitution(value) {
        this._nullCheck();
        this._float32Ptr.array[112 /* RigidBodyConstructionInfoOffsets.Restitution */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The linear sleeping threshold of the rigid body
     */
    get linearSleepingThreshold() {
        this._nullCheck();
        return this._float32Ptr.array[116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */];
    }
    set linearSleepingThreshold(value) {
        this._nullCheck();
        this._float32Ptr.array[116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The angular sleeping threshold of the rigid body
     */
    get angularSleepingThreshold() {
        this._nullCheck();
        return this._float32Ptr.array[120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */];
    }
    set angularSleepingThreshold(value) {
        this._nullCheck();
        this._float32Ptr.array[120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */ / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * The collision group of the rigid body
     *
     * collision group is stored as 16-bit unsigned integer
     */
    get collisionGroup() {
        this._nullCheck();
        return this._uint16Ptr.array[124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */ / 2 /* Constants.A16BytesPerElement */];
    }
    set collisionGroup(value) {
        this._nullCheck();
        this._uint16Ptr.array[124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */ / 2 /* Constants.A16BytesPerElement */] = value;
    }
    /**
     * The collision mask of the rigid body
     *
     * collision mask is stored as 16-bit unsigned integer
     */
    get collisionMask() {
        this._nullCheck();
        return this._uint16Ptr.array[126 /* RigidBodyConstructionInfoOffsets.CollisionMask */ / 2 /* Constants.A16BytesPerElement */];
    }
    set collisionMask(value) {
        this._nullCheck();
        this._uint16Ptr.array[126 /* RigidBodyConstructionInfoOffsets.CollisionMask */ / 2 /* Constants.A16BytesPerElement */] = value;
    }
    /**
     * The additional damping of the rigid body
     */
    get additionalDamping() {
        this._nullCheck();
        return !!this._uint8Ptr.array[128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */ / 1 /* Constants.A8BytesPerElement */];
    }
    set additionalDamping(value) {
        this._nullCheck();
        this._uint8Ptr.array[128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */ / 1 /* Constants.A8BytesPerElement */] = +value;
    }
    /**
     * The no contact response of the rigid body
     */
    get noContactResponse() {
        this._nullCheck();
        return !!this._uint8Ptr.array[129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */ / 1 /* Constants.A8BytesPerElement */];
    }
    set noContactResponse(value) {
        this._nullCheck();
        this._uint8Ptr.array[129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */ / 1 /* Constants.A8BytesPerElement */] = +value;
    }
    /**
     * The disable deactivation of the rigid body
     */
    get disableDeactivation() {
        this._nullCheck();
        return !!this._uint8Ptr.array[130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */ / 1 /* Constants.A8BytesPerElement */];
    }
    set disableDeactivation(value) {
        this._nullCheck();
        this._uint8Ptr.array[130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */ / 1 /* Constants.A8BytesPerElement */] = +value;
    }
}
